import React from 'react';
import Link from 'next/link';
import { useTheme } from '../../hooks/useTheme';
import { Typography } from '../Typography';
import styles from './Button.module.scss';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'text' | 'outline' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  label?: string;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right' | 'both';
  fullWidth?: boolean;
  loading?: boolean;
  // When set, the button navigates to this path. It renders as a real
  // next/link-backed <a> (not a <button>) so the destination is a proper
  // link — reachable via ctrl/cmd-click, middle-click, and crawlers.
  href?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      label,
      children,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      loading = false,
      className = '',
      disabled,
      href,
      ...props
    },
    ref
  ) => {
    const { theme } = useTheme();

    const buttonClasses = [
      styles.button,
      styles[variant],
      styles[size],
      !fullWidth && styles.autoWidth,
      loading && styles.loading,
      disabled && styles.disabled,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const renderIcon = () => {
      if (loading) {
        return (
          <div className={styles.spinner}>
            <div className={styles.spinnerInner} />
          </div>
        );
      }
      return icon;
    };

    const buttonStyle = {
      // Colors
      '--button-bg':
        variant === 'primary'
          ? theme.colors.palettes.brand[600]
          : variant === 'secondary'
            ? theme.colors.palettes.grayLight[950]
            : 'transparent',
      '--button-bg-hover':
        variant === 'primary'
          ? theme.colors.palettes.brand[700]
          : variant === 'secondary'
            ? theme.colors.palettes.grayLight[900]
            : theme.colors.palettes.grayLight[25],
      '--button-bg-active':
        variant === 'primary'
          ? theme.colors.palettes.brand[700]
          : variant === 'secondary'
            ? theme.colors.palettes.grayLight[800]
            : theme.colors.palettes.grayLight[50],
      '--button-text':
        variant === 'primary'
          ? theme.colors.palettes.grayModern[50]
          : variant === 'secondary'
            ? theme.colors.palettes.grayLight[25]
            : theme.colors.palettes.brand[600],
      '--button-border':
        variant === 'primary'
          ? 'transparent'
          : variant === 'secondary'
            ? theme.colors.palettes.grayLight[800]
            : 'transparent',

      // Button specific styles from theme
      '--button-border-radius': theme.button.borderRadius[size],
      '--button-padding-horizontal': theme.button.padding[size].horizontal,
      '--button-padding-vertical': theme.button.padding[size].vertical,
      '--button-font-size': theme.button.fontSize[size],
      '--button-font-weight': theme.button.fontWeight,
      '--button-min-height': theme.button.minHeight[size],
      '--button-transition': theme.button.transition,
      '--button-box-shadow': theme.button.boxShadow.default,
      '--button-box-shadow-hover': theme.button.boxShadow.hover,
      '--button-box-shadow-active': theme.button.boxShadow.active,
    } as React.CSSProperties;

    const content = (
      <>
        {(icon || loading) && (iconPosition === 'left' || iconPosition === 'both') && (
          <span style={{ marginRight: label ? '0.25rem' : '0' }}>{renderIcon()}</span>
        )}
        <span className={styles.content}>
          {label ? (
            <Typography variant="text-sm" as="span" color="inherit" weight="semibold">
              {label}
            </Typography>
          ) : (
            children
          )}
        </span>
        {(icon || loading) && (iconPosition === 'right' || iconPosition === 'both') && (
          <span style={{ marginLeft: label ? '0.25rem' : '0' }}>{renderIcon()}</span>
        )}
      </>
    );

    if (href && !disabled && !loading) {
      const { onClick, ...anchorProps } = props as React.AnchorHTMLAttributes<HTMLAnchorElement> &
        typeof props;
      return (
        <Link href={href}>
          <a
            ref={ref as unknown as React.Ref<HTMLAnchorElement>}
            className={buttonClasses}
            style={buttonStyle}
            onClick={onClick as unknown as React.MouseEventHandler<HTMLAnchorElement>}
            {...anchorProps}
          >
            {content}
          </a>
        </Link>
      );
    }

    return (
      <button
        ref={ref}
        className={buttonClasses}
        disabled={disabled || loading}
        style={buttonStyle}
        {...props}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
